import React    from 'react';
import View     from '@widesk-ui/components/View';
import Alert    from '@widesk-ui/components/Alert';
import Button   from '@widesk-ui/components/Button';
import { describeRegion }        from '@/TokamapEditor/helpers/planAnalysisRegion';
import PlanAnalysisRegionModal   from '@/TokamapEditor/ux/PlanAnalysisRegionModal';
import type { PlanRegion }       from '@models/map/dxfTypes';
import type { PlanSourceResult } from '@models/map/dxfTypes';

/**
 * Étape « zone d'analyse » : l'opérateur entoure la partie du plan à détecter.
 *
 * ## Pourquoi cette étape existe
 *
 * Un plan scanné ou photographié traîne tout ce qui n'est pas le plan — cartouche, légende,
 * marges, logos. Le mal n'est pas seulement esthétique : ces éléments polluent la palette de
 * couleurs, faussent la médiane des aires et la marche qui sépare les numéros des stands, et
 * fabriquent de faux calques dans l'écran suivant. Comme ces grandeurs sont calculées sur TOUT
 * le document, un cartouche dans un coin déforme la détection partout.
 *
 * Un DXF portant plusieurs halls sur une même planche pose exactement le même problème : la
 * zone vaut pour tous les formats.
 *
 * ## Elle est FACULTATIVE
 *
 * Sans zone, tout le document est analysé — le comportement d'avant. C'est le cas nominal d'un
 * export propre, et le bouton « Tout le plan » y ramène à tout moment.
 *
 * ## Le repère
 *
 * Le panneau ne montre pas le plan : le tracé se fait dans une modale plein écran
 * ({@link PlanAnalysisRegionModal}), en pixels NATURELS de l'aperçu — c'est le serveur qui
 * dépixellise, avec le repère qu'il a gelé en fabriquant cette image.
 */
type Props = {
	source: PlanSourceResult;
	/** Zone retenue, en pixels NATURELS de l'aperçu. `null` = tout le plan. */
	region: PlanRegion | null;
	disabled?: boolean;
	/** Ouvre la modale dès l'affichage : c'est le geste attendu juste après le dépôt du plan. */
	openOnMount?: boolean;
	onChange: (region: PlanRegion | null) => void;
};

const PlanAnalysisRegion = ({ source, region, disabled, openOnMount, onChange }: Props) => {
	const [editing, setEditing] = React.useState(Boolean(openOnMount));

	return (
		<View gap={8}>
			<Alert
				type="info"
				showIcon
				message="Limiter la détection à une zone (facultatif)"
				description={
					'Entourez la partie du plan à importer pour écarter cartouche, légende et marges — '
					+ 'ils fabriquent de faux calques et faussent la détection des formes. '
					+ 'Sans zone, tout le plan est analysé.'
				}
			/>

			<View row gap={8} center>
				<View flex size={12} color={region ? undefined : '#999'}>
					{describeRegion(region, source)}
				</View>
				<Button size="small" disabled={disabled || !region} onClick={() => onChange(null)}>
					Tout le plan
				</Button>
			</View>

			<Button disabled={disabled} onClick={() => setEditing(true)}>
				{region ? 'Modifier la zone' : 'Délimiter une zone'}
			</Button>

			<PlanAnalysisRegionModal
				open={editing}
				source={source}
				region={region}
				onApply={onChange}
				onClose={() => setEditing(false)}
			/>
		</View>
	);
};

export default PlanAnalysisRegion;
